import { MODE_CARDS, type ModeCard } from './copy.js';

/**
 * The four policy modes as selection CARDS (directive §3). Each card is a real
 * `<input type="radio">` wrapped in its label, so selection is native keyboard
 * behaviour (arrow keys within the group, space to select) and every card's
 * accessible name is its plain-language title — the L107 bar, by construction
 * rather than by ARIA repair.
 *
 * The wire enum value appears ONLY inside the per-card advanced-details
 * disclosure; primary copy is product language throughout.
 */
export function ModeCards({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (mode: string) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="How location works on shifts"
      style={{ display: 'grid', gap: 'var(--space-12)' }}
    >
      {MODE_CARDS.map((card) => (
        <ModeCardRow
          key={card.value}
          card={card}
          selected={value === card.value}
          disabled={disabled}
          onSelect={() => onChange(card.value)}
        />
      ))}
    </div>
  );
}

const FACETS: readonly { key: keyof ModeCard['facets']; heading: string }[] = [
  { key: 'collected', heading: 'What is collected' },
  { key: 'when', heading: 'When' },
  { key: 'worker', heading: 'What the worker experiences' },
  { key: 'admin', heading: 'What you see' },
  { key: 'blocking', heading: 'Can anything block a punch?' },
  { key: 'privacy', heading: 'Privacy' },
  { key: 'exceptions', heading: 'Exceptions' },
];

function ModeCardRow({
  card,
  selected,
  disabled,
  onSelect,
}: {
  card: ModeCard;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className="rn-card"
      style={{
        display: 'block',
        cursor: disabled ? 'default' : 'pointer',
        borderColor: selected ? 'var(--color-navy)' : undefined,
        margin: 0,
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'baseline' }}>
        <input
          type="radio"
          name="location-policy-mode"
          checked={selected}
          disabled={disabled}
          onChange={onSelect}
          aria-label={card.title}
        />
        <div>
          <span style={{ fontWeight: 600 }}>{card.title}</span>
          {card.isDefault ? (
            <span className="rn-muted" style={{ marginLeft: 'var(--space-8)', fontSize: 13 }}>
              Product default
            </span>
          ) : null}
          <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
            {card.summary}
          </p>
        </div>
      </div>
      <dl
        style={{
          margin: 'var(--space-12) 0 0',
          display: 'grid',
          gap: 'var(--space-6)',
          fontSize: 13,
        }}
      >
        {FACETS.map((facet) => (
          <div key={facet.key}>
            <dt style={{ fontWeight: 600, display: 'inline' }}>{facet.heading}: </dt>
            <dd className="rn-muted" style={{ display: 'inline', margin: 0 }}>
              {card.facets[facet.key]}
            </dd>
          </div>
        ))}
      </dl>
      <details style={{ marginTop: 'var(--space-8)' }}>
        <summary className="rn-muted" style={{ cursor: 'pointer', fontSize: 13 }}>
          Advanced details
        </summary>
        <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-4)' }}>
          Stored as mode <code>{card.value}</code> on the organization’s{' '}
          <code>location_policy</code> row.
        </p>
      </details>
    </label>
  );
}
